import { Link } from 'react-router-dom'
import { CONSENT_REOPEN_EVENT, getMeasurementId } from '@/lib/analytics/consent'
import { SUPPORT_EMAIL, SUPPORT_MAILTO, companyCopyright } from '@/lib/company'
import styles from './AppFooter.module.css'
import workspace from './WorkspaceFooter.module.css'

/**
 * THE COMPACT FOOTER (owner, 2026-09-06) — built in the club storefront's idiom
 * rather than the site's: no three columns of platform links, just the shortest
 * honest tail. Named for the workspace, where it was born; since 2026-09-07 the
 * PLAYER surfaces render it too — the same footer, the same contents, not a
 * second one to keep in step.
 *
 * WHY NOT AppFooter, in the workspace. The workspace is somebody's desk. A footer
 * there advertising our pricing, our guides and our marketplace to the person
 * already paying for the software is selling to a customer who has bought, and it
 * is a wall of links between them and the work.
 *
 * WHY NOT AppFooter, on the player surfaces — a different reason for the same
 * shape. /clubs, /players, /tournaments and /ladders are the product, not the
 * landing page (owner, 2026-09-07), and three columns of platform links is the
 * loudest piece of marketing chrome on them.
 *
 * AND WHY NOT EVEN A ROW OF THEM. This footer briefly carried a `siteLinks` prop:
 * one slim line of Product / Pricing / Solutions / Guides / About us, so that
 * taking the marketing bar off the player surfaces would not also take away the
 * internal links those pages gave the marketing routes. The owner saw it and
 * ruled the same day that the row goes too — "убрать, оставить только powered by
 * TheRacketCenter.com" — having been told plainly that it costs those pages their
 * inbound link from our busiest public pages. So the prop is gone rather than
 * defaulted off: with no way to pass the links in, no surface can quietly grow
 * them back. What is left is what a product page owes anybody: the way home, and
 * the legal tail. marketingChrome.test.mjs holds that line from both sides.
 *
 * WHY NOT StorefrontFooter ITSELF. That component is addressed to a club — it
 * takes a `StorefrontIdentity` and prints that club's address, contact and
 * copyright — and the app shell has no club in hand (it renders over /profile and
 * /admin as much as over /workspace). So this is the same idiom, not the same
 * component: it reuses AppFooter's CSS module exactly as the storefront's does,
 * so the workspace footer sits on the same hairline and the same rail offset the
 * content column takes (see shellRail.test.mjs) — a different set of things in a
 * footer the site already has.
 *
 * "POWERED BY" IS A DOOR, NOT A CREDIT. Removing Home from the workspace menu
 * took away the way back to the public site from inside the desk, so this line
 * has to carry it: the wordmark links to `/`, and it is now the only outward link
 * here on every surface that draws this footer. The legal tail stays for the same
 * reason the storefront keeps it — Privacy and Terms govern the account and the
 * data, and Cookie settings is the control the consent banner requires.
 *
 * WHO YOU ARE PAYING, AND HOW TO REACH THEM (owner, 2026-09-09: "копирайт нужен и
 * вообще делай как надо как у профессионалов"). Stripping the link row also took
 * out the company copyright and the support address, which were never marketing:
 * they are the two things a page somebody pays through owes them, and /clubs,
 * /players, /tournaments, /ladders, /play and the whole workspace are exactly
 * that page. They came back — from `@/lib/company`, the one place either footer
 * reads them, so the site footer and this one cannot say different things.
 *
 * AND THIS IS NOT THE ROW COMING BACK. The row was five links to pages that SELL
 * the platform; this is the seller's own name and inbox. The distinction is the
 * whole of the owner's rule, and marketingChrome.test.mjs asserts both halves of
 * it on the same render: the entity and the address present, and not one of the
 * five marketing routes named anywhere on the page.
 *
 * HELP IS SUPPORT, NOT SELLING (owner, 2026-09-24, docs/ux/2026-09-24-help-center.md
 * §11). It follows the support address because it answers the same need — "how do I"
 * — and it is how a signed-out player on /clubs finds a guide. It is not one of the
 * five marketing routes, so the rule above still holds.
 *
 * THE SHAPE IS AppFooter'S BOTTOM BAR, not a third arrangement — the identity stack
 * (the wordmark that is also the door, the copyright beneath it) over the obligations
 * in one wrapping row, both centered (owner, 2026-09-24). The club storefront's
 * bottom bar is the same shape with the club's name in the stack. Three footers,
 * three sets of contents, one hierarchy.
 */
export function WorkspaceFooter() {
  const analyticsConfigured = Boolean(getMeasurementId())
  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <div className={workspace.row}>
          <div className={styles.brand}>
            <span className={styles.poweredBy}>
              powered by <Link to="/">TheRacketCenter.com</Link>
            </span>
            <span className={styles.copy}>{companyCopyright()}</span>
          </div>
          <div className={styles.bottomLinks}>
            <a className={styles.contact} href={SUPPORT_MAILTO}>
              {SUPPORT_EMAIL}
            </a>
            <Link to="/help">Help</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            {analyticsConfigured ? (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => window.dispatchEvent(new Event(CONSENT_REOPEN_EVENT))}
              >
                Cookie settings
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </footer>
  )
}
